import Cookies from 'js-cookie'

const TokenKey = 'Admin-Token'
const COOKIE_PATH = '/'

// ============================================================
// 过期策略（2026-10-26 业务口径定稿）
// ------------------------------------------------------------------
// 存储介质统一为 Cookie：同一浏览器的所有标签页/窗口共享同一份 cookie，
// 因此「已登录状态下再开一个后台窗口」不会要求重复登录。
//
// - 勾选「保持登录」：固定 15 天有效，关闭浏览器仍保留
// - 不勾「保持登录」（默认）：无操作 24 小时过期（滑动窗口）
//   · 每次请求成功后刷新有效期，所以「只要还在用就一直有效」
//   · 连续 24 小时不操作 → 不再刷新 → 过期需要重新登录
//   · 实现上不再用「会话 cookie」，因为会话 cookie 的失效依赖浏览器行为
//     （macOS Chrome 开启「继续浏览上次打开的网页」时可能永不失效），
//     改为固定 24 小时更可控。
// ============================================================

const KEEP_DAYS = 15 // 保持登录：15 天
const IDLE_HOURS = 24 // 不保持登录：无操作 24 小时
// 刷新节流：距上次刷新不足该间隔则跳过，避免每个请求都写一次 cookie
const REFRESH_THROTTLE_MS = 5 * 60 * 1000

// 记录当前 token 采用的过期策略：'1' = 保持登录，'0' = 无操作 24 小时
// 用 localStorage 而非 cookie，避免污染 cookie 列表；它不敏感，跨窗口共享即可
const KEEP_FLAG_KEY = 'Admin-Token-Keep'
const LAST_REFRESH_KEY = 'Admin-Token-RefreshAt'

function isKeepLogin() {
  try {
    return localStorage.getItem(KEEP_FLAG_KEY) === '1'
  } catch (e) {
    return false
  }
}

function setKeepFlag(keepLogin) {
  try {
    localStorage.setItem(KEEP_FLAG_KEY, keepLogin ? '1' : '0')
  } catch (e) {
    /* 隐私模式下 localStorage 可能不可写，忽略即可 */
  }
}

function clearKeepFlag() {
  try {
    localStorage.removeItem(KEEP_FLAG_KEY)
    localStorage.removeItem(LAST_REFRESH_KEY)
  } catch (e) {
    /* ignore */
  }
}

/** 按当前策略的过期天数/小时数写 cookie */
function writeTokenCookie(token) {
  const attrs = { path: COOKIE_PATH }
  if (isKeepLogin()) {
    attrs.expires = KEEP_DAYS
  } else {
    attrs.expires = IDLE_HOURS / 24 // js-cookie 的 expires 单位是天
  }
  Cookies.set(TokenKey, token, attrs)
}

/**
 * 读取登录 token
 *
 * 历史遗留：早期版本在「不勾选保持登录」时把 token 写在 sessionStorage，
 * 而 sessionStorage 是按标签页隔离的，导致新开窗口读不到 token 必须重新登录。
 * 这里做一次惰性迁移：Cookie 里没有、但 sessionStorage 有时，把旧 token 搬到
 * cookie 并清掉旧值，使老标签页刷新一次后即可被其他窗口共享。
 */
export function getToken() {
  const fromCookie = Cookies.get(TokenKey)
  if (fromCookie) return fromCookie

  const legacy = sessionStorage.getItem(TokenKey)
  if (legacy) {
    // 旧值来源不明确，按默认策略（无操作 24 小时）接管
    setKeepFlag(false)
    writeTokenCookie(legacy)
    sessionStorage.removeItem(TokenKey)
    return legacy
  }

  return ''
}

/**
 * 写入登录 token
 *
 * @param {string} token
 * @param {boolean} keepLogin 是否「保持登录」
 */
export function setToken(token, keepLogin) {
  // 切换策略前先清掉旧 cookie，避免上一策略残留的过期时间仍然生效
  Cookies.remove(TokenKey, { path: COOKIE_PATH })
  setKeepFlag(!!keepLogin)
  writeTokenCookie(token)
  // 清掉旧版本遗留在 sessionStorage 里的 token，避免两处不一致
  sessionStorage.removeItem(TokenKey)
  return token
}

/**
 * 刷新 token 有效期（滑动窗口）
 *
 * 由 @/utils/request 在每次请求成功后调用：
 * 「有操作」→ 续期 → 连续 24 小时不操作才会过期。
 *
 * 已做节流，最多每 5 分钟写一次 cookie。
 */
export function refreshToken() {
  const token = Cookies.get(TokenKey)
  if (!token) return ''
  const now = Date.now()
  try {
    const last = Number(localStorage.getItem(LAST_REFRESH_KEY) || 0)
    if (last && now - last < REFRESH_THROTTLE_MS) return token
    localStorage.setItem(LAST_REFRESH_KEY, String(now))
  } catch (e) {
    /* localStorage 不可用时退化为每次刷新，不影响正确性 */
  }
  writeTokenCookie(token)
  return token
}

export function removeToken() {
  sessionStorage.removeItem(TokenKey)
  Cookies.remove(TokenKey, { path: COOKIE_PATH })
  clearKeepFlag()
  return
}
